import { createReducer, on } from '@ngrx/store';

import { DynamicTicketType, DynamicTicketTypePmsSettings } from '@app/services/dynamic-ticket-type.service';
import { RestProjectTypeModel } from '@main-application/management/pages/service-configuration/project-types/project-types.interface';

import {
  clearCreatedNewProject,
  clearDynamicTicketCreationModal,
  clearDynamicTickets,
  clearProjectTypes,
  createDynamicTicketSuccess,
  createDynamicTicketTypePmsSettingsSuccess,
  createProjectTypeSuccess,
  deleteDynamicTicketSuccess,
  deleteDynamicTicketTypePmsSettingsSuccess,
  deleteProjectTypeSuccess,
  dynamicTicketsLoadedSuccess,
  dynamicTicketsLoading,
  editDynamicTicketSuccess,
  editProjectTypesSuccess,
  getServiceConfig,
  getServiceConfigError,
  getServiceConfigSuccess,
  loadDynamicTicketTypePmsSettings,
  loadDynamicTicketTypePmsSettingsFailed,
  loadDynamicTicketTypePmsSettingsSuccess,
  loadDynamicTickets,
  loadDynamicTicketsFailed,
  loadDynamicTicketsSuccess,
  loadProjectTypes,
  loadProjectTypesFailed,
  loadProjectTypesSuccess,
  openDynamicTicketCreationModal,
  openProjectTypeCreationModal,
  setServiceSectionExpanded,
  updateDynamicTicketTypePmsSettingsSuccess,
  updateTicketTypeNotificationSettingsSuccess,
} from './service-configuration.actions';

export const serviceConfigurationFeatureKey = 'serviceConfiguration';

export interface State {
  expandedSections: {
    Statuses: boolean;
    Resident: boolean;
    Triggers: boolean;
    ticketTypes: boolean;
    workOrderTypes: boolean;
    projectTypes: boolean;
    appointmentTypes: boolean;
  };
  dynamicTicketLoading?: boolean;
  dynamicTickets: DynamicTicketType[];
  openDynamicTicketCreationModal: { create: number; workOrder: boolean };
  projectTypesLoading?: boolean;
  projectTypes: RestProjectTypeModel[];
  createdNewProject: RestProjectTypeModel;
  openProjectTypeCreationModal: number;
  dynamicTicketTypePmsSettingsLoading?: Record<number, boolean>;
  dynamicTicketTypePmsSettings: Record<number, DynamicTicketTypePmsSettings[]>;
}

export const initialState: State = {
  expandedSections: {
    Statuses: true,
    Resident: false,
    Triggers: false,
    ticketTypes: false,
    workOrderTypes: false,
    projectTypes: false,
    appointmentTypes: false,
  },
  dynamicTicketLoading: false,
  dynamicTickets: null,
  openDynamicTicketCreationModal: null,
  openProjectTypeCreationModal: null,
  projectTypesLoading: false,
  projectTypes: null,
  dynamicTicketTypePmsSettingsLoading: {},
  dynamicTicketTypePmsSettings: {},
  createdNewProject: null,
};

export const reducer = createReducer(
  initialState,
  on(getServiceConfig, (state, action) => ({ ...state, serviceConfigLoading: false })),
  on(getServiceConfigSuccess, (state, action) => ({
    ...state,
    serviceConfigLoading: false,
    serviceConfig: action.serviceConfig,
  })),
  on(getServiceConfigError, (state, action) => ({
    ...state,
    serviceConfigLoading: false,
    serviceConfig: null,
  })),
  on(setServiceSectionExpanded, (state, action) => {
    const isTicketOrWorkOrderType = action.section === 'ticketTypes' || action.section === 'workOrderTypes';

    if (action.state) {
      if (isTicketOrWorkOrderType) {
        const collapsed: State['expandedSections'] = {
          Statuses: false,
          Resident: false,
          Triggers: false,
          ticketTypes: state.expandedSections.ticketTypes,
          workOrderTypes: state.expandedSections.workOrderTypes,
          projectTypes: false,
          appointmentTypes: false,
        };
        return {
          ...state,
          expandedSections: { ...collapsed, [action.section]: true },
        };
      } else {
        const collapsed: State['expandedSections'] = {
          Statuses: false,
          Resident: false,
          Triggers: false,
          ticketTypes: false,
          workOrderTypes: false,
          projectTypes: false,
          appointmentTypes: false,
        };
        return {
          ...state,
          expandedSections: { ...collapsed, [action.section]: true },
        };
      }
    } else {
      return {
        ...state,
        expandedSections: { ...state.expandedSections, [action.section]: false },
      };
    }
  }),
  on(dynamicTicketsLoading, state => ({
    ...state,
    dynamicTicketLoading: true,
  })),
  on(dynamicTicketsLoadedSuccess, state => ({
    ...state,
    dynamicTicketLoading: false,
  })),
  on(openDynamicTicketCreationModal, (state, action) => ({
    ...state,
    openDynamicTicketCreationModal: action,
  })),
  on(clearDynamicTicketCreationModal, state => ({
    ...state,
    openDynamicTicketCreationModal: null,
  })),

  on(loadDynamicTickets, state => ({ ...state, dynamicTicketLoading: true })),
  on(loadDynamicTicketsSuccess, (state, action) => ({
    ...state,
    dynamicTicketLoading: false,
    dynamicTickets: action.items,
  })),
  on(loadDynamicTicketsFailed, (state, action) => ({
    ...state,
    dynamicTicketLoading: false,
    dynamicTickets: [],
  })),
  on(createDynamicTicketSuccess, (state, newItem) => ({
    ...state,
    dynamicTickets: [...state.dynamicTickets, newItem],
  })),
  on(editDynamicTicketSuccess, (state, updatedItem) => ({
    ...state,
    createDiligenceLoading: false,
    dynamicTickets: state.dynamicTickets.map(item => (item.id === updatedItem.id ? updatedItem : item)),
  })),
  on(updateTicketTypeNotificationSettingsSuccess, (state, { ticketType }) => ({
    ...state,
    dynamicTickets: state.dynamicTickets
      ? state.dynamicTickets.map(item => (item.id === ticketType.id ? ticketType : item))
      : state.dynamicTickets,
  })),
  on(deleteDynamicTicketSuccess, (state, action) => ({
    ...state,
    dynamicTickets: [...state.dynamicTickets?.filter(el => el.id !== action.id)],
  })),
  on(clearDynamicTickets, state => ({ ...state, dynamicTickets: null })),

  on(loadProjectTypes, state => ({ ...state, projectTypesLoading: true })),
  on(loadProjectTypesSuccess, (state, action) => ({
    ...state,
    projectTypesLoading: false,
    projectTypes: action.items,
  })),
  on(loadProjectTypesFailed, (state, action) => ({
    ...state,
    projectTypesLoading: false,
    projectTypes: [],
  })),
  on(openProjectTypeCreationModal, (state, action) => ({
    ...state,
    openProjectTypeCreationModal: action.create,
  })),
  on(createProjectTypeSuccess, (state, newItem) => ({
    ...state,
    createdNewProject: newItem,
    projectTypes: [...state.projectTypes, newItem],
  })),
  on(editProjectTypesSuccess, (state, updatedItem) => ({
    ...state,
    projectTypes: state.projectTypes.map(item => (item.id === updatedItem.id ? updatedItem : item)),
  })),
  on(deleteProjectTypeSuccess, (state, action) => ({
    ...state,
    projectTypes: [...state.projectTypes?.filter(el => el.id !== action.id)],
  })),
  on(clearProjectTypes, state => ({ ...state, projectTypes: null })),
  on(clearCreatedNewProject, state => ({ ...state, createdNewProject: null })),

  on(loadDynamicTicketTypePmsSettings, (state, action) => ({
    ...state,
    dynamicTicketTypePmsSettingsLoading: {
      ...state.dynamicTicketTypePmsSettingsLoading,
      [action.dynamicTicketTypeId]: true,
    },
  })),
  on(loadDynamicTicketTypePmsSettingsSuccess, (state, action) => ({
    ...state,
    dynamicTicketTypePmsSettingsLoading: {
      ...state.dynamicTicketTypePmsSettingsLoading,
      [action.dynamicTicketTypeId]: false,
    },
    dynamicTicketTypePmsSettings: {
      ...state.dynamicTicketTypePmsSettings,
      [action.dynamicTicketTypeId]: action.items,
    },
  })),
  on(loadDynamicTicketTypePmsSettingsFailed, (state, action) => ({
    ...state,
    dynamicTicketTypePmsSettingsLoading: {
      ...state.dynamicTicketTypePmsSettingsLoading,
      [action.dynamicTicketTypeId]: false,
    },
    dynamicTicketTypePmsSettings: {
      ...state.dynamicTicketTypePmsSettings,
      [action.dynamicTicketTypeId]: [],
    },
  })),
  on(createDynamicTicketTypePmsSettingsSuccess, (state, newItem) => {
    const ticketTypeId = newItem.dynamicTicketTypeId;
    const existingSettings = state.dynamicTicketTypePmsSettings[ticketTypeId] || [];
    return {
      ...state,
      dynamicTicketTypePmsSettings: {
        ...state.dynamicTicketTypePmsSettings,
        [ticketTypeId]: [...existingSettings, newItem],
      },
    };
  }),
  on(updateDynamicTicketTypePmsSettingsSuccess, (state, updatedItem) => {
    const ticketTypeId = updatedItem.dynamicTicketTypeId;
    const existingSettings = state.dynamicTicketTypePmsSettings[ticketTypeId] || [];
    return {
      ...state,
      dynamicTicketTypePmsSettings: {
        ...state.dynamicTicketTypePmsSettings,
        [ticketTypeId]: existingSettings.map(item => (item.id === updatedItem.id ? updatedItem : item)),
      },
    };
  }),
  on(deleteDynamicTicketTypePmsSettingsSuccess, (state, action) => {
    const updatedSettings = { ...state.dynamicTicketTypePmsSettings };
    Object.keys(updatedSettings).forEach(ticketTypeId => {
      updatedSettings[Number(ticketTypeId)] = updatedSettings[Number(ticketTypeId)].filter(el => el.id !== action.id);
    });
    return {
      ...state,
      dynamicTicketTypePmsSettings: updatedSettings,
    };
  })
);
